import {
  Children,
  type ComponentProps,
  type JSXElementConstructor,
  type ReactElement,
  type ReactNode,
  cloneElement,
  isValidElement,
  useMemo,
} from 'react';

import { isEmptyReactNode } from '../utils/is-empty-react-node';

type DefaultElement = keyof JSX.IntrinsicElements | JSXElementConstructor<any>;

export interface UseSlotContentPropsInterface<ElementType extends DefaultElement = 'div'> {
  /** The children nodes to be processed. */
  children: ReactNode;

  /** If true, finds and clones the first valid ReactElement child. Defaults to false. */
  asChild?: boolean;

  /** Callback returning props to merge onto the cloned child (if `asChild`). */
  childProps?: (child: ReactElement) => Partial<ComponentProps<ElementType>> | undefined;

  /** Optional function to wrap/transform the final content. */
  template?: (childContent: ReactNode) => ReactNode;

  /** Optional content overriding `children` or `child.props.children`. Takes precedence. */
  alternativeContent?: ReactNode;

  /** Content rendered if the primary source (`children`, `alternativeContent`) is empty. */
  fallback?: ReactNode;

  /** Additional dependencies for `useMemo`. */
  dependencies?: Array<any>;
}

/**
 * Processes children, implementing the `asChild` pattern with support for templates,
 * fallbacks, alternative content sources, and prop merging via `childProps`. Memoized via `useMemo`.
 *
 * @template ElementType The expected type of the underlying element when `asChild` is true. Defaults to 'div'.
 * @param {UseSlotContentPropsInterface<ElementType>} props Hook configuration.
 * @returns {ReactNode} Processed content or cloned element with processed content.
 */
export function useSlotContent<ElementType extends DefaultElement = 'div'>({
  children,
  asChild = false,
  childProps,
  template,
  alternativeContent,
  fallback,
  dependencies = [],
}: UseSlotContentPropsInterface<ElementType>): ReactNode {
  return useMemo(() => {
    if (!asChild) {
      const contentToUse = alternativeContent !== undefined ? alternativeContent : children;
      // Use fallback only if primary content is effectively empty.
      const finalContent =
        isEmptyReactNode(contentToUse) && fallback !== undefined ? fallback : contentToUse;
      return template ? template(finalContent) : finalContent;
    }

    // --- asChild Logic ---
    const childrenArray = Children.toArray(children);
    // Find the first *element* child, skipping text nodes, nulls etc.
    const foundChildElement = childrenArray.find(isValidElement);

    if (!foundChildElement) {
      return null;
    }

    // Type assertion needed for reliable access to `props.children` after `isValidElement`.
    const child = foundChildElement as ReactElement<{ children?: ReactNode }>;

    const contentToUse =
      alternativeContent !== undefined ? alternativeContent : child.props.children;
    // Use fallback only if inner content source is effectively empty.
    const finalContent =
      isEmptyReactNode(contentToUse) && fallback !== undefined ? fallback : contentToUse;
    const transformedContent = template ? template(finalContent) : finalContent;

    // Clone the target child, merge `childProps`, and inject processed content.
    return cloneElement(
      child,
      childProps ? { ...childProps(child) } : undefined,
      transformedContent,
    );
    // eslint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps)
  }, [children, asChild, childProps, template, alternativeContent, fallback, ...dependencies]);
}
